"use client"

import { useEffect, useRef } from 'react'
import { useUserStore } from '@/stores/user-store'
import { subscribeToAuthStateChange } from '@/lib/services/auth'

interface UserStoreProviderProps {
  children: React.ReactNode
}

/**
 * User Store Provider - Supabase 세션 기반
 *
 * 역할:
 * - 앱 로드 시 Server Action에서 사용자 데이터 가져오기
 * - Supabase 인증 상태 변경 리스너 (로그아웃만 처리)
 * - 인증되지 않은 경우 로그아웃 상태로 처리
 */
export function UserStoreProvider({ children }: UserStoreProviderProps) {
  const { refreshUserData, clearUserData, isInitialized } = useUserStore()
  const initStartedRef = useRef(false)

  // 초기화 — 앱 로드 시 1회만 실행
  useEffect(() => {
    if (isInitialized || initStartedRef.current) return
    initStartedRef.current = true

    const initializeUserData = async () => {
      try {
        // 기존 localStorage 데이터 정리 (1회 실행)
        if (typeof window !== 'undefined') {
          const legacyData = localStorage.getItem('user-store')
          if (legacyData) {
            localStorage.removeItem('user-store')
          }
        }

        await refreshUserData()
      } catch (error) {
        console.error('[UserStoreProvider] Failed to initialize:', error)
      }
    }

    initializeUserData()
  }, [isInitialized, refreshUserData])

  // Supabase 인증 상태 변경 리스너
  // SIGNED_OUT만 처리 — 로그아웃 시 스토어 클리어
  // SIGNED_IN, INITIAL_SESSION, TOKEN_REFRESHED → 모두 무시
  // (초기 데이터 로드는 위 useEffect에서 처리, 로그인 후에는 로그인 페이지에서 직접 refreshUserData 호출)
  useEffect(() => {
    const subscription = subscribeToAuthStateChange(async (event) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[UserStoreProvider] Auth event:', event)
      }
      if (event === 'SIGNED_OUT') {
        clearUserData()
      }
      // SIGNED_IN, INITIAL_SESSION, TOKEN_REFRESHED → 무시
      // 초기화는 위 useEffect에서, 로그인은 로그인 페이지에서 직접 처리
    })

    return () => subscription.unsubscribe()
  }, [clearUserData])

  return <>{children}</>
}
